import { useQuery } from "@apollo/client/react";
import { useEffect } from "react";
import NotFoundScreen from "../404.tsx";
import { CallType } from "../api/graphql.ts";
import { useServerEvent } from "../lib/serverEventsProvider.tsx";
import CallScreen from "./CallScreen.tsx";
import CallSkeleton from "./CallSkeleton";
import { GetCallInfoDocument } from "./GetCallInfo.generated.ts";
import { sendToParent } from "./iframeMessaging.ts";
import RoomScreen from "./RoomScreen.tsx";
import { useCallPolling } from "./useCallPolling.ts";
import { useCallToken } from "./useCallToken.ts";

export default function IsolatedCallScreen() {
  const { callToken, encodedToken } = useCallToken();

  const { data, loading, error, refetch } = useQuery(GetCallInfoDocument, {
    fetchPolicy: "no-cache", // do not cache access token
    context: {
      headers: {
        "x-call-token": callToken,
      },
    },
    variables: { callId: undefined },
  });

  useEffect(() => {
    if (error) sendToParent("USER_UNAUTHORIZED");
  }, [error]);

  const call = data?.call.call;
  const currentParticipant = data?.call.currentParticipant;

  // poll for meeting information. mostly useful for on-demand meetings but
  // also for incidents where CVH doesn't start on time.
  // This does not bother voice calls.
  useCallPolling({
    call,
    refetch,
    intervalMs: call?.callType === CallType.Room ? 30000 : 10000,
  });

  // This event is emitted in createVoiceCallHandler, for all users, including the scheduler.
  // This does not interfere with the call polling or with Rooms.
  useServerEvent("newVoiceCall", ({ callId: newCallId }) => {
    if (call && newCallId === call.id) refetch();
  });

  if (error) return <NotFoundScreen />;

  // loading or transitioning
  if (loading || !call || !currentParticipant) return <CallSkeleton />;

  return call.callType === CallType.Room ? (
    <RoomScreen
      allowRating={false}
      call={call}
      currentParticipant={currentParticipant}
      fromPath={`/isolated-call/${encodedToken}`}
      standalone
    />
  ) : (
    <CallScreen
      allowRating={false}
      call={call}
      currentParticipant={currentParticipant}
    />
  );
}
